import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useRouteLoaderData,
} from '@remix-run/react';
import type { LinksFunction } from '@remix-run/node';
import { Toaster } from 'sonner';
import { DEFAULT_THEME, THEME_ROUTE_ID } from '~/lib/theme';
import type { loader as appLoader } from '~/routes/app';
import './tailwind.css';

export const links: LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap',
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  // Only the /app layout knows the user and their flags. Every page outside it (login,
  // onboarding, the notifications iframe) renders light, and so does the root error screen:
  // React Router drops the /app layout's data once an error bubbles past it.
  const theme = useRouteLoaderData<typeof appLoader>(THEME_ROUTE_ID)?.theme ?? DEFAULT_THEME;

  return (
    <html lang="en" data-theme={theme}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <Toaster
          theme={theme}
          richColors
          position="top-center"
          style={{ '--width': '480px' } as React.CSSProperties}
        />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <div>Something went wrong</div>;
};

function App() {
  return <Outlet />;
}

export default withSentry(App);
